import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import authService from '@/services/auth'

export interface RouteMeta {
  requiresAuth?: boolean
  requiresRole?: 'admin' | 'accountant' | 'user'
  requiresAnyRole?: Array<'admin' | 'accountant' | 'user'>
  guestOnly?: boolean
  requiresInvoicing?: boolean
  requiresInvoiceIssuance?: boolean
  /** Route exists only while package gating is switched on platform-wide. */
  requiresSubscriptionsEnforced?: boolean
}

export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const authStore = useAuthStore()
  const meta = to.meta as RouteMeta

  if (!authStore.user && authService.getToken()) {
    await authStore.initialize()
  }

  authStore.checkTokenExpiry()

  if (meta.guestOnly && authStore.isAuthenticated) {
    return next('/app')
  }

  if (meta.requiresAuth && !authStore.isAuthenticated) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }

  if (meta.requiresRole && !authStore.hasRole(meta.requiresRole)) {
    return next('/unauthorized')
  }

  if (meta.requiresAnyRole && !authStore.hasAnyRole(meta.requiresAnyRole)) {
    return next('/unauthorized')
  }

  // `invoicing_locked` is the third way in: the firm has the module, but this
  // client's package does not include it. Without it the locked invoicing page
  // could never render — the guard would bounce the very user it is for.
  if (
    meta.requiresInvoicing
    && authStore.user?.invoicing_enabled !== true
    && authStore.user?.invoice_archive_available !== true
    && authStore.user?.invoicing_locked !== true
  ) {
    return next('/unauthorized')
  }

  if (meta.requiresSubscriptionsEnforced && authStore.user?.subscriptions_enforced !== true) {
    return next('/unauthorized')
  }

  if (meta.requiresInvoiceIssuance && authStore.user?.invoicing_enabled !== true) {
    return next('/unauthorized')
  }

  next()
}

export function roleGuard(requiredRole: 'admin' | 'accountant' | 'user') {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ): void => {
    const authStore = useAuthStore()

    if (!authStore.hasRole(requiredRole)) {
      return next('/unauthorized')
    }

    next()
  }
}

export function adminGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore()

  if (!authStore.isAdmin) {
    return next('/unauthorized')
  }

  next()
}

export function accountantGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore()

  if (!authStore.isAccountant) {
    return next('/unauthorized')
  }

  next()
}
